/**
 * Qualified Leads tab for the GHL Qualifier Location Detail page.
 *
 * Combines the qualified-feed table, the qualified-stat cards, the outcome
 * filter, and a debounced search input over contact name/email/ID. Search is
 * client-side because the page size is bounded (50 rows) — no need for a
 * server-side ILIKE clause yet.
 */

import { useMemo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { QualifierStatCards } from './QualifierStatCards'
import { QualifiedLeadsTable } from './QualifiedLeadsTable'
import { QualifierPagination } from './QualifierPagination'
import type {
  QualifiedLeadFeedRow,
  QualifierStats,
} from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly stats: QualifierStats | null | undefined
  readonly statsLoading: boolean
  readonly data: readonly QualifiedLeadFeedRow[]
  readonly count: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly outcomeFilter: string
  readonly searchTerm: string
  readonly onPageChange: (page: number) => void
  readonly onOutcomeChange: (outcome: string) => void
  readonly onSearchChange: (term: string) => void
  readonly onRowClick: (id: string) => void
  /** When true, the location delivers directly to Google Ads — show the per-row "Test upload" button. */
  readonly showTestButton?: boolean
  /** When true, render a per-row Google Ads upload status badge. */
  readonly showUploadStatus?: boolean
}

const OUTCOME_OPTIONS = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'pending', label: 'Pending' },
] as const

export function QualifiedLeadsTab({
  stats,
  statsLoading,
  data,
  count,
  page,
  pageSize,
  isLoading,
  outcomeFilter,
  searchTerm,
  onPageChange,
  onOutcomeChange,
  onSearchChange,
  onRowClick,
  showTestButton,
  showUploadStatus,
}: Props) {
  // Search runs over the visible page only — same trade-off the existing GHL
  // dashboard uses. With pageSize=50 this still feels instant for operators.
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return data
    return data.filter((row) => {
      const haystack = [
        row.contact_name,
        row.contact_email,
        row.contact_phone,
        row.contact_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [data, searchTerm])

  return (
    <div className="space-y-4">
      <QualifierStatCards mode="qualified" stats={stats} isLoading={statsLoading} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={outcomeFilter} onValueChange={onOutcomeChange}>
            <SelectTrigger className="w-[180px] h-9" aria-label="Filter by outcome">
              <SelectValue placeholder="Outcome" />
            </SelectTrigger>
            <SelectContent>
              {OUTCOME_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">Auto-refreshing every 15 seconds</p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search contacts"
            className="pl-9"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search contacts"
          />
        </div>
      </div>

      <QualifiedLeadsTable
        data={filtered}
        isLoading={isLoading}
        onRowClick={onRowClick}
        showTestButton={showTestButton}
        showUploadStatus={showUploadStatus}
      />

      <QualifierPagination
        page={page}
        pageSize={pageSize}
        count={count}
        onPageChange={onPageChange}
      />
    </div>
  )
}
